import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import Swal from 'sweetalert2';
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  XCircle,
  GitBranch,
  History,
  Eye,
  MessageSquare,
} from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { RichTextEditor } from '../../components/editorial/RichTextEditor';
import { markdownEmphasisToHtml } from '../../utils/richTextDisplay';
import { useAuthStore } from '../../store/authStore';
import {
  editorialWorkflowService,
  EditedQuestionPayload,
  StageVersion,
  StageVersionContent,
  EditorialTaskState,
} from '../../services/editorial-workflow.service';

/**
 * Editorial review screen (NWFE-RQ-01.2 / 01.3)
 * Side-by-side: reference version (read-only) vs editable version with a
 * rich text editor. Completing a stage stores immutable before/after
 * versions; all versions remain accessible in the task history.
 */

const STATE_LABELS: Record<EditorialTaskState, string> = {
  pending_stage_1: 'Pending Stage 1 — Grammar',
  pending_stage_2: 'Pending Stage 2 — Conceptual',
  reviewed: 'Reviewed',
  rejected: 'Rejected',
  published: 'Published',
};

const STATE_BADGE: Record<EditorialTaskState, string> = {
  pending_stage_1: 'bg-amber-50 border-amber-200 text-amber-700',
  pending_stage_2: 'bg-blue-50 border-blue-200 text-blue-700',
  reviewed: 'bg-green-50 border-green-200 text-green-700',
  rejected: 'bg-red-50 border-red-200 text-red-700',
  published: 'bg-purple-50 border-purple-200 text-purple-700',
};

const VERSION_LABELS: Record<string, string> = {
  original: 'Original',
  post_stage_1: 'Post-Stage 1 (grammar)',
  post_stage_2: 'Post-Stage 2 (conceptual)',
};

// ── Content helpers ──────────────────────────────────────────────────────────

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Plain generated text → simple HTML paragraphs for display/editing */
function toHtml(value: string | undefined | null): string {
  if (!value) return '';
  if (/<[a-z][^>]*>/i.test(value)) return value; // already HTML (stage versions)
  return value
    .split(/\n{2,}/)
    // Markdown emphasis (*italic*, **bold**) from the AI/generator renders as
    // real formatting instead of exposing the markers
    .map((block) => `<p>${markdownEmphasisToHtml(escapeHtml(block)).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

function renderHtml(html: string | undefined | null) {
  const clean = DOMPurify.sanitize(toHtml(html || ''));
  // Italic takes priority over bold when both wrap the same term (client rule)
  return {
    __html: clean
      .replace(/<strong>\s*(<em>[\s\S]*?<\/em>)\s*<\/strong>/gi, '$1')
      .replace(/<em>\s*<strong>([\s\S]*?)<\/strong>\s*<\/em>/gi, '<em>$1</em>'),
  };
}

interface EditState {
  stem: string;
  explanation: string;
  alternatives: Array<{ letter: string; text: string; isCorrect?: boolean }>;
  initialContext?: string;
  caseContext?: string;
}

function contentToEditState(content: StageVersionContent): EditState {
  return {
    stem: toHtml(content.stem),
    explanation: toHtml(content.explanation),
    alternatives: (content.alternatives || []).map((a) => ({
      letter: a.letter,
      text: toHtml(a.text),
      isCorrect: a.isCorrect,
    })),
    initialContext: content.initialContext ? toHtml(content.initialContext) : undefined,
    caseContext: content.caseContext ? toHtml(content.caseContext) : undefined,
  };
}

function questionToContent(question: any): StageVersionContent {
  return {
    stem: question?.stem || '',
    explanation: question?.explanation || '',
    correctAnswer: question?.correctAnswer,
    alternatives: (question?.alternatives || []).map((a: any) => ({
      letter: a.letter,
      text: a.text || '',
      isCorrect: !!a.isCorrect,
    })),
    initialContext: question?.initialContext || undefined,
    caseContext: question?.caseContext || undefined,
  };
}

// ── Read-only content pane ───────────────────────────────────────────────────

function ContentPane({ content, title, subtitle }: { content: StageVersionContent; title: string; subtitle?: string }) {
  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5">
      <div className="flex items-center gap-2 mb-4">
        <Eye className="w-4 h-4 text-gray-400" />
        <h3 className="text-sm font-bold text-gray-700">{title}</h3>
        {subtitle && <span className="text-xs text-gray-400">{subtitle}</span>}
      </div>

      {content.caseContext && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Case context</p>
          <div className="prose prose-sm max-w-none text-gray-700 bg-gray-50 rounded-xl p-3" dangerouslySetInnerHTML={renderHtml(content.caseContext)} />
        </div>
      )}
      {content.initialContext && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Initial context</p>
          <div className="prose prose-sm max-w-none text-gray-700 bg-gray-50 rounded-xl p-3" dangerouslySetInnerHTML={renderHtml(content.initialContext)} />
        </div>
      )}

      <div className="mb-4">
        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Question (stem)</p>
        <div className="prose prose-sm max-w-none text-gray-800 bg-gray-50 rounded-xl p-3" dangerouslySetInnerHTML={renderHtml(content.stem)} />
      </div>

      {(content.alternatives || []).length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Alternatives</p>
          <div className="space-y-2">
            {content.alternatives!.map((alt) => (
              <div
                key={alt.letter}
                className={`flex gap-2 rounded-xl border p-2.5 text-sm ${
                  alt.isCorrect ? 'border-green-200 bg-green-50/60' : 'border-gray-100 bg-gray-50'
                }`}
              >
                <span className={`font-bold ${alt.isCorrect ? 'text-green-700' : 'text-gray-500'}`}>
                  {alt.letter})
                </span>
                <div className="prose prose-sm max-w-none text-gray-700 flex-1" dangerouslySetInnerHTML={renderHtml(alt.text)} />
                {alt.isCorrect && <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />}
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Explanation</p>
        <div className="prose prose-sm max-w-none text-gray-700 bg-gray-50 rounded-xl p-3" dangerouslySetInnerHTML={renderHtml(content.explanation)} />
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ReviewTaskDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const permissions: string[] = user?.permissions || [];

  const { data: task, isLoading, error } = useQuery({
    queryKey: ['editorial-task', id],
    queryFn: () => editorialWorkflowService.getTask(id!),
    enabled: !!id,
  });

  const { data: versions = [] } = useQuery({
    queryKey: ['editorial-task-versions', id],
    queryFn: () => editorialWorkflowService.getTaskVersions(id!),
    enabled: !!id,
  });

  const { data: history = [] } = useQuery({
    queryKey: ['editorial-task-history', id],
    queryFn: () => editorialWorkflowService.getTaskHistory(id!),
    enabled: !!id,
  });

  const { data: config } = useQuery({
    queryKey: ['editorial-config'],
    queryFn: () => editorialWorkflowService.getConfig(),
    staleTime: 10 * 60 * 1000,
  });

  // Which stage is being reviewed here (null = read-only)
  const activeStage: 1 | 2 | null =
    task?.state === 'pending_stage_1' && permissions.includes('review_stage_1')
      ? 1
      : task?.state === 'pending_stage_2' && permissions.includes('review_stage_2')
        ? 2
        : null;

  const versionByLabel = useMemo(() => {
    const map: Record<string, StageVersion> = {};
    for (const v of versions) map[v.label] = v;
    return map;
  }, [versions]);

  // Reference (left pane): stage 1 reviews the original; stage 2 reviews the
  // Stage 1 output (01.3: "side-by-side view: the Stage 1 output (read-only)")
  const referenceContent: StageVersionContent | null = useMemo(() => {
    if (!task) return null;
    if (task.state === 'pending_stage_2') {
      return versionByLabel['post_stage_1']?.content || questionToContent(task.question);
    }
    return versionByLabel['original']?.content || questionToContent(task.question);
  }, [task, versionByLabel]);

  const referenceTitle =
    task?.state === 'pending_stage_2' ? 'Stage 1 output (read-only)' : 'Original (read-only)';

  // Editable state (right pane)
  const [edit, setEdit] = useState<EditState | null>(null);
  const [observations, setObservations] = useState('');

  useEffect(() => {
    if (referenceContent && activeStage && !edit) {
      setEdit(contentToEditState(referenceContent));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceContent, activeStage]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['editorial-task', id] });
    queryClient.invalidateQueries({ queryKey: ['editorial-task-versions', id] });
    queryClient.invalidateQueries({ queryKey: ['editorial-task-history', id] });
    queryClient.invalidateQueries({ queryKey: ['editorial-tasks'] });
  };

  const completeMutation = useMutation({
    mutationFn: async () => {
      const payload: EditedQuestionPayload | undefined = edit
        ? {
            stem: edit.stem,
            explanation: edit.explanation,
            alternatives: edit.alternatives.map((a) => ({ letter: a.letter, text: a.text })),
            initialContext: edit.initialContext,
            caseContext: edit.caseContext,
          }
        : undefined;

      if (activeStage === 1) {
        return editorialWorkflowService.completeStage1(id!, observations || undefined, payload);
      }
      return editorialWorkflowService.completeStage2(id!, payload);
    },
    onSuccess: () => {
      invalidate();
      setEdit(null);
      Swal.fire({
        title: activeStage === 1 ? 'Stage 1 completed' : 'Stage 2 completed',
        text:
          activeStage === 1
            ? 'The question moved to Stage 2 — Conceptual Review.'
            : 'The question is now Reviewed (ready for CRM publication).',
        icon: 'success',
        confirmButtonColor: '#1e73be',
      });
    },
    onError: (err: any) => {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Unexpected error',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    },
  });

  const handleComplete = async () => {
    const result = await Swal.fire({
      title: activeStage === 1 ? 'Complete Stage 1 — Grammar Review?' : 'Complete Stage 2 — Conceptual Review?',
      text: 'Both the reference version and your edited version will be stored in the task history.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Complete Stage ${activeStage}`,
      confirmButtonColor: '#16a34a',
    });
    if (result.isConfirmed) completeMutation.mutate();
  };

  const handleReject = async () => {
    const categories = config?.rejectionCategories || [];
    const options = categories.map((c) => `<option value="${c.key}">${c.label}</option>`).join('');

    const result = await Swal.fire({
      title: 'Reject / report question',
      html: `
        <p style="font-size:14px;color:#555;margin-bottom:8px">The question will leave the workflow and feed the generator's improvement loop.</p>
        <select id="rejectCategory" class="swal2-select" style="width:80%">${options}</select>
        <textarea id="rejectComment" class="swal2-textarea" placeholder="Comment (optional)"></textarea>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Reject',
      confirmButtonColor: '#dc2626',
      preConfirm: () => {
        const select = document.getElementById('rejectCategory') as HTMLSelectElement;
        const textarea = document.getElementById('rejectComment') as HTMLTextAreaElement;
        if (!select?.value) {
          Swal.showValidationMessage('Please select a category');
          return false;
        }
        return { category: select.value, comment: textarea?.value.trim() || undefined };
      },
    });

    if (!result.isConfirmed || !result.value) return;

    try {
      await editorialWorkflowService.rejectTask(id!, result.value.category, result.value.comment);
      invalidate();
      navigate('/editorial/review-tasks');
    } catch (err: any) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Unexpected error',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  // Version history viewer (tabs)
  const [selectedVersion, setSelectedVersion] = useState<string>('original');
  const availableVersions = versions.map((v) => v.label);
  useEffect(() => {
    if (availableVersions.length && !availableVersions.includes(selectedVersion as any)) {
      setSelectedVersion(availableVersions[availableVersions.length - 1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions]);

  if (isLoading) {
    return (
      <div className="p-10 flex items-center justify-center gap-2 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Loading review task...</span>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="p-10 text-center text-sm text-red-500">
        {(error as any)?.response?.data?.error || 'Review task not found.'}
        <div className="mt-4">
          <Link to="/editorial/review-tasks" className="text-[#1e73be] font-semibold">
            ← Back to Review Tasks
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-24">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/editorial/review-tasks"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#1e73be] mb-2"
        >
          <ArrowLeft className="w-4 h-4" /> Review Tasks
        </Link>
        <div className="flex items-center justify-between">
          <PageHeader
            title="Question Review"
            description="Side-by-side editorial review with tracked versions"
            breadcrumbs={[{ label: 'Editorial Review' }, { label: 'Question Review' }]}
          />
          <span className={`inline-flex px-3 py-1.5 rounded-xl border text-sm font-semibold ${STATE_BADGE[task.state]}`}>
            {STATE_LABELS[task.state]}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
          <span className="px-2 py-1 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 font-semibold">
            OE: {task.questionSummary.oeCode || '—'}
          </span>
          <span className="px-2 py-1 rounded-lg bg-gray-50 border border-gray-200 text-gray-600">
            {task.questionSummary.type} · {task.questionSummary.difficulty}
          </span>
          {task.questionSummary.isVersion && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-50 border border-purple-200 text-purple-700">
              <GitBranch className="w-3 h-3" /> Version
            </span>
          )}
          {task.stage1Assignee && (
            <span className="px-2 py-1 rounded-lg bg-amber-50 border border-amber-200 text-amber-700">
              Stage 1: {task.stage1Assignee.name}
            </span>
          )}
          {task.stage2Assignee && (
            <span className="px-2 py-1 rounded-lg bg-blue-50 border border-blue-200 text-blue-700">
              Stage 2: {task.stage2Assignee.name}
            </span>
          )}
        </div>
      </div>

      {/* Stage 1 observations — visible to the Stage 2 reviewer (01.2 AC-4) */}
      {task.stage1Observations && task.state !== 'pending_stage_1' && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare className="w-4 h-4 text-amber-600" />
            <p className="text-sm font-bold text-amber-800">Stage 1 reviewer observations</p>
          </div>
          <p className="text-sm text-amber-900 whitespace-pre-wrap">{task.stage1Observations}</p>
        </div>
      )}

      {(task.question?.subQuestions?.length || 0) > 0 && (
        <div className="mb-6 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
          This is a case-study question with {task.question.subQuestions.length} sub-question(s).
          Sub-questions are shown in the Question Bank editor; this review covers the case stem,
          context, alternatives and explanation.
        </div>
      )}

      {/* Side-by-side review */}
      {activeStage && referenceContent && edit ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
          <ContentPane content={referenceContent} title={referenceTitle} />

          {/* Editable version */}
          <div className="bg-white rounded-2xl shadow-md border border-[#1e73be]/30 p-5">
            <h3 className="text-sm font-bold text-gray-700 mb-4">
              Your edited version (Stage {activeStage})
            </h3>

            {edit.caseContext !== undefined && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Case context</p>
                <RichTextEditor
                  value={edit.caseContext || ''}
                  onChange={(html) => setEdit((e) => (e ? { ...e, caseContext: html } : e))}
                />
              </div>
            )}
            {edit.initialContext !== undefined && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Initial context</p>
                <RichTextEditor
                  value={edit.initialContext || ''}
                  onChange={(html) => setEdit((e) => (e ? { ...e, initialContext: html } : e))}
                />
              </div>
            )}

            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Question (stem)</p>
              <RichTextEditor
                value={edit.stem}
                onChange={(html) => setEdit((e) => (e ? { ...e, stem: html } : e))}
              />
            </div>

            {edit.alternatives.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Alternatives</p>
                <div className="space-y-2">
                  {edit.alternatives.map((alt, idx) => (
                    <div key={alt.letter} className="flex gap-2 items-start">
                      <span
                        className={`font-bold text-sm mt-3 ${alt.isCorrect ? 'text-green-700' : 'text-gray-500'}`}
                        title={alt.isCorrect ? 'Correct answer' : undefined}
                      >
                        {alt.letter})
                      </span>
                      <div className="flex-1">
                        <RichTextEditor
                          compact
                          value={alt.text}
                          onChange={(html) =>
                            setEdit((e) => {
                              if (!e) return e;
                              const alternatives = [...e.alternatives];
                              alternatives[idx] = { ...alternatives[idx], text: html };
                              return { ...e, alternatives };
                            })
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Explanation</p>
              <RichTextEditor
                value={edit.explanation}
                onChange={(html) => setEdit((e) => (e ? { ...e, explanation: html } : e))}
              />
            </div>

            {activeStage === 1 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                  Observations for the Stage 2 reviewer (optional)
                </p>
                <textarea
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  rows={3}
                  placeholder="Notes visible to the conceptual reviewer..."
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:border-[#1e73be] outline-none resize-y"
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        // Read-only mode: show the latest available content
        referenceContent && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
            <ContentPane
              content={
                versionByLabel['post_stage_2']?.content ||
                versionByLabel['post_stage_1']?.content ||
                referenceContent
              }
              title="Current version (read-only)"
              subtitle={
                task.state === 'rejected'
                  ? `Rejected at Stage ${task.rejectedAtStage} — ${task.rejectionCategory}`
                  : undefined
              }
            />
            <div className="space-y-6">
              {/* Version history */}
              {versions.length > 0 && (
                <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <GitBranch className="w-4 h-4 text-gray-400" />
                    <h3 className="text-sm font-bold text-gray-700">Versions</h3>
                  </div>
                  <div className="flex gap-2 mb-4">
                    {versions.map((v) => (
                      <button
                        key={v.label}
                        onClick={() => setSelectedVersion(v.label)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                          selectedVersion === v.label
                            ? 'bg-[#1e73be] text-white border-[#1e73be]'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-[#1e73be]'
                        }`}
                      >
                        {VERSION_LABELS[v.label] || v.label}
                      </button>
                    ))}
                  </div>
                  {versionByLabel[selectedVersion] && (
                    <ContentPane
                      content={versionByLabel[selectedVersion].content}
                      title={VERSION_LABELS[selectedVersion] || selectedVersion}
                      subtitle={`${versionByLabel[selectedVersion].createdByName} · ${new Date(versionByLabel[selectedVersion].createdAt).toLocaleString()}`}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )
      )}

      {/* Versions while reviewing (below the side-by-side) */}
      {activeStage && versions.length > 0 && (
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <GitBranch className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-bold text-gray-700">Version history</h3>
          </div>
          <div className="flex gap-2 mb-4">
            {versions.map((v) => (
              <button
                key={v.label}
                onClick={() => setSelectedVersion(v.label)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                  selectedVersion === v.label
                    ? 'bg-[#1e73be] text-white border-[#1e73be]'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-[#1e73be]'
                }`}
              >
                {VERSION_LABELS[v.label] || v.label}
              </button>
            ))}
          </div>
          {versionByLabel[selectedVersion] && (
            <ContentPane
              content={versionByLabel[selectedVersion].content}
              title={VERSION_LABELS[selectedVersion] || selectedVersion}
              subtitle={`${versionByLabel[selectedVersion].createdByName} · ${new Date(versionByLabel[selectedVersion].createdAt).toLocaleString()}`}
            />
          )}
        </div>
      )}

      {/* Transition timeline */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-bold text-gray-700">Task history</h3>
        </div>
        <div className="space-y-2">
          {history.length === 0 && <p className="text-sm text-gray-400">No history yet.</p>}
          {history.map((h) => (
            <div key={h._id} className="flex items-start gap-3 border-b border-gray-50 pb-2">
              <div className="w-2 h-2 rounded-full bg-[#1e73be] mt-1.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-700">
                  {h.action === 'assignment'
                    ? `Stage ${h.meta?.stage} assigned to ${h.meta?.assigneeName || '—'}`
                    : h.fromState
                      ? `${STATE_LABELS[h.fromState]} → ${STATE_LABELS[h.toState]}`
                      : `Task created (${STATE_LABELS[h.toState]})`}
                </p>
                {h.meta?.category && (
                  <p className="text-xs text-red-600">
                    Category: {h.meta.category}
                    {h.meta?.comment ? ` — ${h.meta.comment}` : ''}
                  </p>
                )}
                <p className="text-xs text-gray-400">
                  {new Date(h.at).toLocaleString()} · {h.byUserName}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sticky action bar */}
      {activeStage && (
        <div className="fixed bottom-0 left-72 right-0 bg-white/95 backdrop-blur border-t border-gray-200 px-8 py-4 flex items-center justify-between z-40">
          <p className="text-xs text-gray-500">
            Completing Stage {activeStage} stores both the reference and your edited version
            permanently in the task history.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReject}
              className="px-5 py-2.5 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 font-semibold text-sm transition-colors flex items-center gap-2"
            >
              <XCircle className="w-4 h-4" /> Reject / report
            </button>
            <button
              onClick={handleComplete}
              disabled={completeMutation.isPending}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold text-sm shadow-lg transition-all flex items-center gap-2 disabled:opacity-50"
            >
              {completeMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              Complete Stage {activeStage}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReviewTaskDetail;
